import { useState, type DragEvent } from 'react';
import { List, ListItem, ListItemButton, ListItemText, type Theme } from '@mui/material';
import DragIndicator from '@mui/icons-material/DragIndicatorOutlined';
import { useTranslation } from 'react-i18next';
import type { FavoriteMenuItem } from '@/hooks/useFavorites';

export interface SidebarFavoritesListProps {
  /** The favorites to show, in the user's order. */
  items: FavoriteMenuItem[];
  isActive: (path: string) => boolean;
  onNavigate: (path: string) => void;
  /** Moves the dragged favorite to where the one it was dropped on stands. */
  onMove: (fromId: string, toId: string) => void;
  /** The sidebar's label size, restated per row so it outranks the theme's ListItemText size. */
  labelFontSize: string;
}

/**
 * The expanded sidebar's favorites. A row is dragged onto another to move it there, with the
 * native drag and drop GroupByChain uses; while dragging, a line on the target row shows the
 * side the dragged one lands on.
 */
export function SidebarFavoritesList({ items, isActive, onNavigate, onMove, labelFontSize }: SidebarFavoritesListProps) {
  const { t } = useTranslation();
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);

  const ids = items.map(({ item }) => item.id);
  const endDrag = () => {
    setDraggedId(null);
    setOverId(null);
  };
  const startDrag = (event: DragEvent, id: string) => {
    event.dataTransfer.effectAllowed = 'move';
    // Firefox starts no drag without data.
    event.dataTransfer.setData('text/plain', id);
    setDraggedId(id);
  };
  const drop = (event: DragEvent, targetId: string) => {
    event.preventDefault();
    if (draggedId) onMove(draggedId, targetId);
    endDrag();
  };
  // Moving down lands below the target, moving up above it.
  const dropLine = (id: string) => {
    if (!draggedId || id !== overId || id === draggedId) return undefined;
    const offset = ids.indexOf(id) > ids.indexOf(draggedId) ? '-2px' : '2px';
    return (theme: Theme) => `inset 0 ${offset} 0 ${theme.palette.primary.main}`;
  };

  return (
    // pl aligns the labels with the module labels below (gutter + icon column)
    <List dense disablePadding sx={{ px: 0.75, pt: 0, pb: 0.5 }}>
      {items.map(({ item }) => (
        <ListItem
          key={`fav-${item.id}`}
          disablePadding
          draggable
          onDragStart={(e) => startDrag(e, item.id)}
          onDragOver={(e) => {
            e.preventDefault();
            setOverId(item.id);
          }}
          onDrop={(e) => drop(e, item.id)}
          onDragEnd={endDrag}
          sx={{
            py: 0,
            opacity: draggedId === item.id ? 0.5 : 1,
            boxShadow: dropLine(item.id),
            '&:hover .favorite-drag-handle': { opacity: 1 },
          }}
        >
          <ListItemButton
            onClick={() => onNavigate(item.path)}
            selected={isActive(item.path)}
            dense
            sx={{
              borderRadius: 1,
              minHeight: 26,
              py: 0,
              pl: 4.25,
              pr: 1,
              '& .MuiListItemText-primary': { fontSize: labelFontSize },
            }}
          >
            {/* In the empty icon column left of the label, so the labels stay aligned with the modules'. */}
            <DragIndicator
              className="favorite-drag-handle"
              titleAccess={t('nav.dragToReorder')}
              sx={{
                position: 'absolute',
                left: 10,
                top: '50%',
                transform: 'translateY(-50%)',
                fontSize: 14,
                opacity: 0,
                color: 'text.disabled',
                transition: 'opacity 0.2s',
              }}
            />
            <ListItemText
              primary={t(item.labelKey || item.label, { defaultValue: item.label })}
              primaryTypographyProps={{ variant: 'body2', noWrap: true }}
            />
          </ListItemButton>
        </ListItem>
      ))}
    </List>
  );
}
